/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.com/docs/reference/config-files/gatsby-browser/
 */

// Import CSS in consistent order to prevent mini-css-extract-plugin ordering warnings
import "./src/styles/App.css"
import "./src/styles/Header.css"
import "./src/styles/Footer.css"
import "./src/styles/E.css"
import "./src/styles/Sell.css"
import "./src/styles/SellComponents.css"

import React from "react"
import { StoreContextProvider } from "./src/context/StoreContext"
import { SearchContextProvider } from "./src/context/SearchContext"
import { saveCartReturnPath, getPageLabelFromPath } from "./src/utils/cartReturnPath"

export const wrapRootElement = ({ element }) => (
  <SearchContextProvider>
    <StoreContextProvider>{element}</StoreContextProvider>
  </SearchContextProvider>
)

const loadNetlifyIdentity = () => {
  if (typeof window === "undefined" || window.__NETLIFY_IDENTITY_LOADING__) return
  window.__NETLIFY_IDENTITY_LOADING__ = true

  const script = document.createElement("script")
  script.src = "https://identity.netlify.com/v1/netlify-identity-widget.js"
  script.async = true
  script.onload = () => {
    if (!window.netlifyIdentity) return
    window.netlifyIdentity.on("init", (user) => {
      if (!user) {
        window.netlifyIdentity.on("login", () => {
          if (!window.location.pathname.startsWith("/admin")) {
            window.location.href = "/admin/"
          }
        })
      }
    })
  }
  document.body.appendChild(script)
}

const shouldLoadNetlifyIdentity = () => {
  const { pathname, hash } = window.location
  // /admin loads Identity in admin.js only — loading it twice breaks the login modal
  if (pathname.startsWith("/admin")) return false
  return (
    hash.includes("invite_token") ||
    hash.includes("recovery_token") ||
    hash.includes("confirmation_token")
  )
}

const injectWebAppManifest = () => {
  if (typeof document === "undefined") return
  if (document.querySelector('link[rel="manifest"]')) return
  const link = document.createElement("link")
  link.rel = "manifest"
  link.href = "/manifest.webmanifest"
  document.head.appendChild(link)
}

// Send initial page view after hydration to reduce main thread blocking
export const onInitialClientRender = () => {
  if (shouldLoadNetlifyIdentity()) {
    loadNetlifyIdentity()
  }

  // Keep the web app manifest off the critical request chain (HTML → manifest).
  // Inject only after the page has fully loaded so it never competes with LCP.
  if (document.readyState === "complete") {
    injectWebAppManifest()
  } else {
    window.addEventListener("load", injectWebAppManifest, { once: true })
  }

  // Defer analytics until after page is interactive
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_path: window.location.pathname,
        })
      }
    }, { timeout: 3000 })
  } else {
    setTimeout(() => {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_path: window.location.pathname,
        })
      }
    }, 1000)
  }
}

// Control scroll behavior on navigation
export const shouldUpdateScroll = ({ routerProps: { location }, prevRouterProps }) => {
  // On first load (no previous location), don't interfere with scroll
  if (!prevRouterProps) {
    return false // Let the browser handle it naturally
  }
  
  const prevPathname = prevRouterProps?.location?.pathname
  
  // For the homepage, don't auto-scroll (preserve scroll position when navigating back)
  if (location.pathname === "/" || location.pathname === "") {
    return false
  }
  
  // For sourcing page: scroll to top ONLY if coming from a different page
  // (not when searching within the sourcing page itself)
  if (location.pathname === "/sourcing/" || location.pathname === "/sourcing") {
    // If navigating from another page (like homepage), scroll to top
    if (prevPathname && prevPathname !== "/sourcing/" && prevPathname !== "/sourcing") {
      return [0, 0]
    }
    // If already on sourcing page (search within page), don't scroll
    return false
  }
  
  // For all other pages, scroll to top
  return [0, 0]
}

export const onRouteUpdate = ({ location, prevLocation }) => {
  if (shouldLoadNetlifyIdentity()) {
    loadNetlifyIdentity()
  }

  if (prevLocation) {
    const prevPath = prevLocation.pathname || ""
    const goingToCart =
      location.pathname === "/cart" || location.pathname === "/cart/"
    const fromCart = prevPath === "/cart" || prevPath === "/cart/"
    if (goingToCart && !fromCart) {
      saveCartReturnPath(prevPath + (prevLocation.search || ""), getPageLabelFromPath(prevPath))
    }
  }

  if (prevLocation == null) {
    // First page load - page view sent by onInitialClientRender
    return
  }

  const prevPathname = prevLocation?.pathname
  
  // For the homepage, don't scroll
  if (location.pathname === "/" || location.pathname === "") {
    return
  }
  
  // For sourcing page: scroll gracefully if coming from a different page
  // Show "Part Number Lookup" heading (scroll past just the sticky header)
  if (location.pathname === "/sourcing/" || location.pathname === "/sourcing") {
    if (prevPathname && prevPathname !== "/sourcing/" && prevPathname !== "/sourcing") {
      setTimeout(() => {
        // Scroll to show the "Part Number Lookup" heading (just below header)
        // Header is ~64px, so scroll to 0 to show the breadcrumb + title
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" })
      }, 50)
    }
    return
  }
  
  // For other paths, scroll to the top with a small delay
  setTimeout(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" })
  }, 20)
}
